/**
 * The **ingress** reading of the shared clause tree (ADR-040 §D-6; REQ-UI-160).
 *
 * Pure, React-free.
 *
 * # The tree is shared; the semantics invert
 *
 * The document model, its clause kinds and every structural operation on them
 * are identical across both directions — ``InterfaceFilterConfig`` and
 * ``PeerFilterConfig`` are the same shape over the same node union — so this
 * module imports the structural half from :file:`@/peers/peer-filter-tree`
 * rather than restating it (CLAUDE.md §4.4). What it does **not** import is the
 * meaning, because the meaning is the exact inverse:
 *
 * | | egress (Flow W) | ingress (Flow V) |
 * |---|---|---|
 * | no document | emits **nothing** (default-deny) | receives **everything** (allow-all) |
 * | a document | widens nothing; it is the whole allow-set | **narrows** intake |
 * | "allow everything" control | deliberately absent | the default, and *removing* a filter is the affordance |
 *
 * ⚠ **Flow W §2.3's "there is no allow-everything control" rule is egress-only
 * and must not be carried across** (REQ-UI-160). On egress, waving the whole
 * firehose through is a code decision so it stays reviewable. On ingress,
 * allow-everything *is* the fail-safe default — dropping sensor data at the
 * perimeter is unrecoverable, because the record exists nowhere else and no
 * later act reconstructs it — so the affordance that needs to exist is the one
 * that returns an interface to accepting everything, and the one that needs
 * friction is the one that starts excluding.
 *
 * Cross-references:
 *   - ADR-040 §D-6 (the filter, allow-all); REQ-PROVIDER-037.
 *   - REQ-UI-160 (the inversion said out loud); Flow V Frame 6.
 *   - `packages/meridian_provider_framework/.../_filter_schema.py` (the document model).
 */
import { canonicalizeClause, hasEmptyAllowSet } from "@/peers/peer-filter-tree";
import type { ClauseNode } from "@/peers/peer-model";
import { FILTER_SCHEMA_VERSION, type ProviderFilterConfig } from "@/providers/provider-model";

/**
 * Build the persistable document from an edited root.
 *
 * Returns ``null`` — not an empty document — when nothing survives
 * canonicalization, because on this surface ``filter: null`` is exactly how the
 * model spells *"this interface accepts everything"* (REQ-PROVIDER-037). That is
 * the same code path the egress builder takes to mean the opposite thing, which
 * is why the two directions get separate functions with separate names rather
 * than one shared helper whose return value means whatever the caller assumed.
 */
export function toProviderFilterConfig(root: ClauseNode | null): ProviderFilterConfig | null {
  const canonical = canonicalizeClause(root);
  if (canonical === null) return null;
  return { schema_version: FILTER_SCHEMA_VERSION, root: canonical, connection_overrides: {} };
}

/** Which of the three intake postures a filter document expresses. */
export type IntakePosture = "receives-everything" | "narrowing" | "starved";

/** What to render above the clause tree: the posture and the sentence for it. */
export interface IntakeReading {
  posture: IntakePosture;
  /** The operator-facing statement, rendered verbatim. */
  text: string;
}

/**
 * How this filter reads as an intake control, in the operator's words.
 *
 * All three postures are **stated rather than implied**, because from the
 * outside they are indistinguishable — an interface receiving everything, an
 * interface narrowed to a slice, and an interface narrowed to nothing all look
 * the same until you know what is supposed to be arriving.
 *
 * - **``receives-everything``** — the fail-safe default. Said out loud because
 *   it is the surprising reading on this surface: on egress "unconfigured" means
 *   *emits nothing*, and an operator carrying that intuition across would read a
 *   blank panel as a closed door.
 * - **``narrowing``** — the standing notice REQ-UI-160 requires on any document
 *   that excludes. What a narrowing filter drops is not stored, not forwarded
 *   and not recoverable, which is a heavier statement than the egress analogue
 *   ever needs to make.
 * - **``starved``** — a clause the operator added and has not filled in. On
 *   egress that silences a flow; here it silences *collection*, and the records
 *   it declines cease to exist anywhere. It is the sharpest failure this surface
 *   can produce and gets the sharpest sentence.
 */
export function describeIntakeReading(root: ClauseNode | null): IntakeReading {
  const canonical = canonicalizeClause(root);
  if (canonical === null) {
    return {
      posture: "receives-everything",
      text: "No filter is configured, so this interface receives everything. Filters narrow intake; a too-narrow filter silently starves collection.",
    };
  }
  if (hasEmptyAllowSet(canonical)) {
    return {
      posture: "starved",
      text: "At least one condition has an empty allow-set, which matches nothing — so this interface would accept nothing at all. Fill it in or remove it; what a filter excludes is not stored anywhere.",
    };
  }
  return {
    posture: "narrowing",
    text: "This filter limits what this interface accepts. Anything it excludes is not stored, not forwarded, and not recoverable.",
  };
}

/**
 * The closing line of the **non-privileged** filter summary (Flow V §7).
 *
 * Inverted from the egress twin deliberately, and the inversion is the point: on
 * egress the surprising reading is *unconfigured means emits nothing*; here it is
 * *unconfigured means accepts everything*. In both cases the surprising one is
 * the one that gets said.
 *
 * @param configured Whether the BFF's ``FilterSummary`` reported a document.
 */
export function describeFilterSummaryReading(configured: boolean): string {
  return configured
    ? "Which values each condition accepts is restricted to administrators and support engineers."
    : "No filter is configured — this interface receives everything.";
}
